import { useState, useMemo } from 'react';
import { CalendarClock, CheckCircle2, Loader2, Phone, Send, Sun, Moon } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { COMPANY, FENCE_TYPES } from '../mock/data';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function todayISO() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString().slice(0, 10);
}
function isoPlusDays(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}
function isSunday(iso) {
  if (!iso) return false;
  // Parse as local date to avoid TZ off-by-one
  const [y, m, day] = iso.split('-').map(Number);
  const d = new Date(y, m - 1, day);
  return d.getDay() === 0;
}
function humanDate(iso) {
  if (!iso) return '';
  const [y, m, day] = iso.split('-').map(Number);
  const d = new Date(y, m - 1, day);
  return d.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

function WindowChip({ value, current, onClick, icon: Icon, label, sub }) {
  const active = value === current;
  return (
    <button
      type="button"
      onClick={() => onClick(value)}
      data-testid={`window-chip-${value.toLowerCase()}`}
      className={`w-full rounded-md border px-4 py-3 text-left transition-all ${
        active
          ? 'border-gold bg-gold/10 shadow-[inset_0_0_0_1px_rgba(212,161,58,0.6)]'
          : 'border-white/15 hover:border-white/30 bg-ink'
      }`}
    >
      <div className="flex items-center gap-2">
        <Icon className={`w-4 h-4 ${active ? 'text-gold' : 'text-cream/60'}`} />
        <span className={`text-sm font-semibold uppercase tracking-wider ${active ? 'text-gold' : 'text-cream'}`}>
          {label}
        </span>
      </div>
      <div className="mt-1 text-xs text-cream/50">{sub}</div>
    </button>
  );
}

export default function BookingForm() {
  const minDate = todayISO();
  const maxDate = isoPlusDays(21);

  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    service: '',
    preferred_date: '',
    preferred_window: 'AM',
    backup_date: '',
    backup_window: 'PM',
    notes: '',
  });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const onChange = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const prefIsSunday = useMemo(() => isSunday(form.preferred_date), [form.preferred_date]);
  const backupIsSunday = useMemo(() => isSunday(form.backup_date), [form.backup_date]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error('Please add your name and phone number so we can call to confirm.');
      return;
    }
    if (!form.preferred_date || !form.backup_date) {
      toast.error('Please pick a preferred date AND a backup date.');
      return;
    }
    if (prefIsSunday || backupIsSunday) {
      toast.error("We don't schedule estimates on Sundays — please pick Mon–Sat.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/bookings`, form);
      setSent(true);
      toast.success("Got it! We'll call to confirm your estimate time shortly.");
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Something went wrong. Please call us directly.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="book"
      data-testid="booking-section"
      className="py-24 lg:py-32 bg-ink border-t border-white/5 relative overflow-hidden"
    >
      <div
        className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{ backgroundImage: 'radial-gradient(circle at 15% 30%, #d4a13a 0%, transparent 45%)' }}
      />

      <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-5 gap-12 items-start relative">
        <div className="lg:col-span-2">
          <div className="eyebrow">Schedule an Estimate</div>
          <h2 className="section-title">Pick a time. We&rsquo;ll confirm by phone.</h2>
          <p className="section-lede">
            Choose the day and window that work for you. We&rsquo;ll call within business hours to
            lock in the exact time &mdash; if your first choice is booked, we&rsquo;ll offer your
            backup or the closest slot.
          </p>

          <div className="mt-8 space-y-4">
            {[
              'On-site written quote — no obligation',
              'Real crew, real timelines, real prices',
              'Free & 100% no-pressure',
            ].map((x) => (
              <div key={x} className="flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-gold" />
                <span className="text-cream/90">{x}</span>
              </div>
            ))}
          </div>

          <div className="mt-10 p-5 rounded-md border border-white/10 bg-ink-panel">
            <div className="text-cream/60 text-xs uppercase tracking-widest">Need it faster?</div>
            <a
              href={`tel:${COMPANY.phoneRaw}`}
              className="mt-1 font-display uppercase text-3xl text-gold inline-flex items-center gap-2 hover:text-gold-light transition-colors"
            >
              <Phone className="w-6 h-6" /> {COMPANY.phone}
            </a>
            <div className="mt-1 text-cream/50 text-sm">{COMPANY.hours}</div>
          </div>
        </div>

        <div className="lg:col-span-3">
          <div className="rounded-md bg-ink-panel border border-white/10 text-cream p-6 md:p-8 shadow-2xl">
            {sent ? (
              <div className="py-8 text-center">
                <CalendarClock className="w-14 h-14 mx-auto text-gold" />
                <h3 className="mt-4 font-display uppercase text-3xl text-cream">
                  Estimate requested!
                </h3>
                <p className="mt-2 text-cream/70">
                  We received your preferred time and will call{' '}
                  <span className="text-cream font-semibold">{form.phone}</span> shortly to confirm.
                </p>
                <div className="mt-6 inline-block text-left bg-ink border border-white/10 rounded-md p-4 text-sm">
                  <div className="text-cream/60 text-xs uppercase tracking-widest mb-2">
                    You requested
                  </div>
                  <div>
                    <span className="text-gold font-semibold">1st choice:</span>{' '}
                    {humanDate(form.preferred_date)} — {form.preferred_window}
                  </div>
                  <div>
                    <span className="text-gold font-semibold">Backup:</span>{' '}
                    {humanDate(form.backup_date)} — {form.backup_window}
                  </div>
                </div>
                <div className="mt-6">
                  <button
                    className="btn-outline"
                    onClick={() => {
                      setSent(false);
                      setForm({
                        name: '', phone: '', email: '', address: '', city: '', service: '',
                        preferred_date: '', preferred_window: 'AM',
                        backup_date: '', backup_window: 'PM', notes: '',
                      });
                    }}
                  >
                    Book another
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-cream/80 text-xs uppercase tracking-widest">Full name *</Label>
                  <Input
                    data-testid="booking-name-input"
                    value={form.name}
                    onChange={(e) => onChange('name', e.target.value)}
                    placeholder="Jane Boudreaux"
                    className="mt-1.5 bg-ink border-white/15 text-cream placeholder:text-cream/30"
                  />
                </div>
                <div>
                  <Label className="text-cream/80 text-xs uppercase tracking-widest">Phone *</Label>
                  <Input
                    type="tel"
                    data-testid="booking-phone-input"
                    value={form.phone}
                    onChange={(e) => onChange('phone', e.target.value)}
                    placeholder="(318) 555-0134"
                    className="mt-1.5 bg-ink border-white/15 text-cream placeholder:text-cream/30"
                  />
                </div>
                <div>
                  <Label className="text-cream/80 text-xs uppercase tracking-widest">Email</Label>
                  <Input
                    type="email"
                    data-testid="booking-email-input"
                    value={form.email}
                    onChange={(e) => onChange('email', e.target.value)}
                    placeholder="you@email.com"
                    className="mt-1.5 bg-ink border-white/15 text-cream placeholder:text-cream/30"
                  />
                </div>
                <div>
                  <Label className="text-cream/80 text-xs uppercase tracking-widest">City</Label>
                  <Input
                    data-testid="booking-city-input"
                    value={form.city}
                    onChange={(e) => onChange('city', e.target.value)}
                    placeholder="Shreveport, LA"
                    className="mt-1.5 bg-ink border-white/15 text-cream placeholder:text-cream/30"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-cream/80 text-xs uppercase tracking-widest">Street address</Label>
                  <Input
                    data-testid="booking-address-input"
                    value={form.address}
                    onChange={(e) => onChange('address', e.target.value)}
                    placeholder="123 Cypress Lane"
                    className="mt-1.5 bg-ink border-white/15 text-cream placeholder:text-cream/30"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-cream/80 text-xs uppercase tracking-widest">Service needed</Label>
                  <Select value={form.service} onValueChange={(v) => onChange('service', v)}>
                    <SelectTrigger
                      data-testid="booking-service-select"
                      className="mt-1.5 bg-ink border-white/15 text-cream"
                    >
                      <SelectValue placeholder="Choose a service" />
                    </SelectTrigger>
                    <SelectContent>
                      {FENCE_TYPES.map((x) => (
                        <SelectItem key={x} value={x}>
                          {x}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Preferred date + window */}
                <div className="sm:col-span-2 pt-4 mt-2 border-t border-white/10">
                  <div className="flex items-center gap-2 text-gold text-xs uppercase tracking-[0.28em] font-semibold">
                    <CalendarClock className="w-4 h-4" /> 1st Choice
                  </div>
                </div>
                <div>
                  <Label className="text-cream/80 text-xs uppercase tracking-widest">Preferred date *</Label>
                  <Input
                    type="date"
                    data-testid="preferred-date-input"
                    min={minDate}
                    max={maxDate}
                    value={form.preferred_date}
                    onChange={(e) => onChange('preferred_date', e.target.value)}
                    className="mt-1.5 bg-ink border-white/15 text-cream placeholder:text-cream/30"
                  />
                  {prefIsSunday && (
                    <div className="mt-1 text-xs text-red-400">Sunday isn&rsquo;t available &mdash; pick Mon–Sat.</div>
                  )}
                </div>
                <div>
                  <Label className="text-cream/80 text-xs uppercase tracking-widest">Preferred window *</Label>
                  <div className="mt-1.5 grid grid-cols-2 gap-2">
                    <WindowChip
                      value="AM"
                      current={form.preferred_window}
                      onClick={(v) => onChange('preferred_window', v)}
                      icon={Sun}
                      label="AM"
                      sub="7:00 AM – 12:00 PM"
                    />
                    <WindowChip
                      value="PM"
                      current={form.preferred_window}
                      onClick={(v) => onChange('preferred_window', v)}
                      icon={Moon}
                      label="PM"
                      sub="12:00 PM – 7:00 PM"
                    />
                  </div>
                </div>

                {/* Backup date + window */}
                <div className="sm:col-span-2 pt-4 mt-2 border-t border-white/10">
                  <div className="flex items-center gap-2 text-gold text-xs uppercase tracking-[0.28em] font-semibold">
                    <CalendarClock className="w-4 h-4" /> Backup Choice
                  </div>
                  <div className="mt-1 text-xs text-cream/50">
                    In case your first choice is already booked.
                  </div>
                </div>
                <div>
                  <Label className="text-cream/80 text-xs uppercase tracking-widest">Backup date *</Label>
                  <Input
                    type="date"
                    data-testid="backup-date-input"
                    min={minDate}
                    max={maxDate}
                    value={form.backup_date}
                    onChange={(e) => onChange('backup_date', e.target.value)}
                    className="mt-1.5 bg-ink border-white/15 text-cream placeholder:text-cream/30"
                  />
                  {backupIsSunday && (
                    <div className="mt-1 text-xs text-red-400">Sunday isn&rsquo;t available &mdash; pick Mon–Sat.</div>
                  )}
                </div>
                <div>
                  <Label className="text-cream/80 text-xs uppercase tracking-widest">Backup window *</Label>
                  <div className="mt-1.5 grid grid-cols-2 gap-2">
                    <WindowChip
                      value="AM"
                      current={form.backup_window}
                      onClick={(v) => onChange('backup_window', v)}
                      icon={Sun}
                      label="AM"
                      sub="7:00 AM – 12:00 PM"
                    />
                    <WindowChip
                      value="PM"
                      current={form.backup_window}
                      onClick={(v) => onChange('backup_window', v)}
                      icon={Moon}
                      label="PM"
                      sub="12:00 PM – 7:00 PM"
                    />
                  </div>
                </div>

                <div className="sm:col-span-2">
                  <Label className="text-cream/80 text-xs uppercase tracking-widest">
                    Anything else we should know?
                  </Label>
                  <Textarea
                    data-testid="booking-notes-input"
                    value={form.notes}
                    onChange={(e) => onChange('notes', e.target.value)}
                    placeholder="Gate width, HOA info, dogs on property, etc."
                    rows={3}
                    className="mt-1.5 bg-ink border-white/15 text-cream placeholder:text-cream/30"
                  />
                </div>

                <div className="sm:col-span-2">
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="booking-submit-btn"
                    className="btn-primary w-full justify-center py-4 disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" /> Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" /> Request my estimate
                      </>
                    )}
                  </button>
                  <p className="mt-3 text-xs text-cream/50 text-center">
                    Your time isn&rsquo;t locked in until we call you back to confirm.
                  </p>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
